import { EventUpdateRequest, NotificationTypes, type SafeAny } from '@/common/types';
import {
  axiosInstance,
  addToQueue,
  setRequestUpdateRequired,
  isRequestUpdateRequired,
  isEventValid
} from '@/common/utils';
import { ScoreSetEvent } from '../../types';
import { findSignalByEventType, getClonedEventIDbySignal } from '@/core/helpers';
import { HYPNOGRAM_EVENTS } from '@/modules/study/common';

const updateEventInBothStores = (
  commit: SafeAny,
  eventData: { id: string; type: string; start: string; duration: number }
) => {
  // optimistically update the event in state
  commit('updateEventInSignalEvents', eventData);
  // also in hypnogram for sleep cycles
  commit('study/updateEventInHypnogramData', eventData, { root: true });
};

export const updateStudyScoreSetEvent = async (
  { state, rootState, commit, dispatch }: SafeAny,
  {
    studyId,
    epochSize = 30,
    eventId,
    type,
    start,
    duration
  }: {
    studyId: string;
    epochSize?: number;
    eventId: string;
    type: string;
    start: string;
    duration: number;
  }
) => {
  const url = `${rootState.env?.requestUrl.events}/studies/${studyId}/events/${eventId}`;

  // current state of the event in signal events
  const signalName = findSignalByEventType(type, state.signalEventMapping);

  const currentSignalEvent = signalName
    ? state.signalEvents[signalName]?.find((event: SafeAny) => event.id === eventId)
    : null;
  // current state of the event in hypnogram data
  const currentHypnogramEvent = rootState.study?.hypnogram?.data?.data?.find(
    (event: SafeAny) => event.id === eventId
  );

  const originalEventData = {
    type: currentSignalEvent?.eventType ?? currentHypnogramEvent?.eventType ?? type,
    start: currentSignalEvent?.start ?? currentHypnogramEvent?.start ?? start,
    duration: currentSignalEvent?.duration ?? currentHypnogramEvent?.duration ?? duration
  };

  const validationMessage = isEventValid(
    type,
    start,
    duration,
    rootState.scoreSet.scoreSet.data,
    state.signalEvents
  );

  if (validationMessage) {
    commit(
      'addNotification',
      {
        i18nTitle: 'errorUpdateScoreSetEvent',
        i18nDescription: validationMessage.key,
        i18nParams: validationMessage.params,
        type: validationMessage.type
      },
      { root: true }
    );
    state.eventActions.errorTimestamp = new Date().getTime();

    return false;
  }

  updateEventInBothStores(commit, {
    id: eventId,
    type,
    start,
    duration
  });

  const executeRequest = async (originalEvent?: ScoreSetEvent): Promise<void> => {
    try {
      let finalEventId = eventId;

      if (isRequestUpdateRequired && originalEvent) {
        const clonedEvent = getClonedEventIDbySignal(originalEvent, state, signalName || '');
        if (clonedEvent?.id) {
          finalEventId = clonedEvent.id;
        }
      } else if (!currentHypnogramEvent && !currentSignalEvent && signalName) {
        const matchedSignal = state.signalEvents[signalName]?.find(
          (signal) => signal.duration === duration && signal.start === start
        );
        if (matchedSignal?.id) {
          finalEventId = matchedSignal.id;
        }
      }

      finalEventId = state.eventIdMap?.[finalEventId] ?? finalEventId;

      if (String(finalEventId).startsWith('temp_')) return;

      if (finalEventId !== eventId) {
        updateEventInBothStores(commit, { id: finalEventId, type, start, duration });
      }

      const resolvedUrl = `${rootState.env?.requestUrl.events}/studies/${studyId}/events/${finalEventId}`;

      const res = await axiosInstance.put(resolvedUrl, { type, start, duration });

      if (res?.status === 205) {
        setRequestUpdateRequired(true);
        await dispatch('scoreSet/getScoreSetByStudy', { sleepStudyID: studyId }, { root: true });
        await dispatch('epoch/getStudyScoreSetEvents', { studyId, epochSize }, { root: true });
      } else if (!HYPNOGRAM_EVENTS.includes(type)) {
        // revert to previous state if response is not 200
        if (currentSignalEvent) {
          commit('updateEventInSignalEvents', currentSignalEvent);
        }
        if (currentHypnogramEvent) {
          commit(
            'study/updateEventInHypnogramData',
            {
              id: currentHypnogramEvent.id,
              type: currentHypnogramEvent.eventType,
              start: currentHypnogramEvent.start,
              duration: currentHypnogramEvent.duration,
              end: currentHypnogramEvent.end
            },
            { root: true }
          );
        }
      }
    } catch (error: SafeAny) {
      // revert to previous state in case of error
      if (currentSignalEvent) {
        commit('updateEventInSignalEvents', currentSignalEvent);
      }
      if (currentHypnogramEvent) {
        commit(
          'study/updateEventInHypnogramData',
          {
            id: currentHypnogramEvent.id,
            type: currentHypnogramEvent.eventType,
            start: currentHypnogramEvent.start,
            duration: currentHypnogramEvent.duration,
            end: currentHypnogramEvent.end
          },
          { root: true }
        );
      }
    }
  };

  addToQueue(
    executeRequest,
    {
      url,
      originalEvent: originalEventData
    } as EventUpdateRequest,
    () => {
      commit(
        'addNotification',
        {
          i18nTitle: 'warningEventLimit',
          i18nDescription: 'warningEventLimitDescription',
          type: NotificationTypes.Warning
        },
        { root: true }
      );
    },
    {
      key: `event:${studyId}:${eventId}`,
      strategy: 'replace'
    }
  );
  return true;
};
